import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { FormDataRecord, FormConfig } from '../conponents/form-data-card/form-data-card.component';
import { GuidService } from './guid.service';
import { environment } from '../services/global-environment';

// API 回應介面
interface ApiResponse<T> {
  success: boolean;
  message: string;
  requestId: string;
  data: T;
  error?: string;
}

/**
 * 表單資料服務
 *
 * 主要功能：
 * - getFormRecords(): 從 API 取得表單容器資料（非同步）
 * - getFormRecordsByComponentName(): 根據組件名稱從 API 取得資料（非同步）
 * - getFormRecordsSync(): 從本地 mock 資料取得（同步，作為備援）
 *
 * API 端點：
 * GET /api/form-manager/get_form_container
 * 參數：companyid, componentname, formid (選填)
 */
@Injectable({
  providedIn: 'root'
})
export class FormDataService {
  private formDataKey = 'form_data_records';
  private mockData: { [formName: string]: FormDataRecord[] } = {};
  private apiUrl = environment.apiUrl;
  private companyid = environment.companyid;

  constructor(
    private guidService: GuidService,
    private http: HttpClient
  ) {
    // this.initializeMockData();
  }

  // 初始化 Mock 資料
  // private initializeMockData(): void {
  //   // 載入已儲存的資料（如果有的話）
  //   const savedData = localStorage.getItem(this.formDataKey);
  //   if (savedData) {
  //     try {
  //       const parsed = JSON.parse(savedData);
  //       // 轉換日期字串回 Date 物件
  //       Object.keys(parsed).forEach(formName => {
  //         parsed[formName] = parsed[formName].map((record: any) => ({
  //           ...record,
  //           createdAt: new Date(record.createdAt),
  //           updatedAt: new Date(record.updatedAt)
  //         }));
  //       });
  //       this.mockData = parsed;
  //     } catch (error) {
  //       console.error('載入表單資料失敗:', error);
  //       this.mockData = {};
  //     }
  //   }

  //   // 如果沒有資料，建立一些範例資料
  //   if (Object.keys(this.mockData).length === 0) {
  //     this.createSampleData();
  //   }
  // }

  // 建立範例資料
  // private createSampleData(): void {
  //   const now = new Date();

  //   // 承攬廠商安全衛生及環保違規罰則一覽表 - 已有一筆資料
  //   this.mockData['contractor-safety-violations'] = [
  //     {
  //       id: this.guidService.generateGuid(),
  //       formName: 'contractor-safety-violations',
  //       data: {
  //         title: '承攬廠商安全衛生及環保違規罰則一覽表',
  //         content: '範例資料：已填寫的違規罰則一覽表'
  //       },
  //       createdAt: new Date(now.getTime() - 86400000), // 1天前
  //       updatedAt: new Date(now.getTime() - 86400000)
  //     }
  //   ];

  //   // 工作人員名單 - 已有兩筆資料
  //   this.mockData['worker-list'] = [
  //     {
  //       id: this.guidService.generateGuid(),
  //       formName: 'worker-list',
  //       data: {
  //         title: '工作人員名單',
  //         workers: ['張三', '李四', '王五']
  //       },
  //       createdAt: new Date(now.getTime() - 172800000), // 2天前
  //       updatedAt: new Date(now.getTime() - 86400000)
  //     },
  //     {
  //       id: this.guidService.generateGuid(),
  //       formName: 'worker-list',
  //       data: {
  //         title: '工作人員名單（第二批）',
  //         workers: ['陳六', '林七']
  //       },
  //       createdAt: new Date(now.getTime() - 86400000),
  //       updatedAt: new Date(now.getTime() - 86400000)
  //     }
  //   ];

  //   // 儲存到 localStorage
  //   this.saveToLocalStorage();
  // }

  // 取得指定表單的所有記錄 (非同步版本，呼叫 API)
  getFormRecords(formid: string): Observable<FormDataRecord[]> {
    const url = `${this.apiUrl}/api/form-manager/get_form_container`;
    const params = {
      companyid: this.companyid,
      formid: formid
    };

    return this.http.get<ApiResponse<any[]>>(url, { params }).pipe(
      map(response => {

        console.log('api/form-manager/get_form_container API 回應:', response);

        if (response.success && Array.isArray(response.data)) {
          // 將 API 回傳的資料轉換為 FormDataRecord 格式
          return response.data.map((item: any) => {
            const formDataRecord: FormDataRecord = {
              id: item.id || this.guidService.generateGuid(),
              formName: formid,
              data: item,
              createdAt: item._ts ? new Date(item._ts * 1000) : new Date(),
              updatedAt: item._ts ? new Date(item._ts * 1000) : new Date()
            };
            return formDataRecord;
          });
        }
        return [];
      }),
      catchError(error => {
        console.error('取得表單記錄失敗:', error);
        // 如果 API 失敗，回傳空陣列或使用本地 mock 資料作為備援
        const fallbackData = this.mockData[formid] || [];
        console.warn(`API 失敗，使用備援資料，共 ${fallbackData.length} 筆記錄`);
        return of(fallbackData);
      })
    );
  }

  // 根據組件名稱取得表單記錄
  getFormRecordsByComponentName(componentname: string, formid?: string): Observable<FormDataRecord[]> {
    const url = `${this.apiUrl}/api/form-manager/get_form_container`;
    const params: any = {
      companyid: this.companyid,
      componentname: componentname
    };

    // 如果提供了 formid，則加入參數
    if (formid) {
      params.formid = formid;
    }

    return this.http.get<ApiResponse<any[]>>(url, { params }).pipe(
      map(response => {
        if (response.success && Array.isArray(response.data)) {
          // 將 API 回傳的資料轉換為 FormDataRecord 格式
          return response.data.map((item: any) => {
            const formDataRecord: FormDataRecord = {
              id: item.id || this.guidService.generateGuid(),
              formName: componentname,
              data: item,
              createdAt: item._ts ? new Date(item._ts * 1000) : new Date(),
              updatedAt: item._ts ? new Date(item._ts * 1000) : new Date()
            };
            return formDataRecord;
          });
        }
        return [];
      }),
      catchError(error => {
        console.error('根據組件名稱取得表單記錄失敗:', error);
        // 如果 API 失敗，回傳空陣列或使用本地 mock 資料作為備援
        const fallbackData = this.mockData[componentname] || [];
        console.warn(`API 失敗，使用備援資料，共 ${fallbackData.length} 筆記錄`);
        return of(fallbackData);
      })
    );
  }

  // 取得指定表單的所有記錄 (同步版本，使用 mock 資料 - 保留作為備用)
  getFormRecordsSync(formid: string): FormDataRecord[] {
    return this.mockData[formid] || [];
  }

  // 新增表單記錄
  addFormRecord(formName: string, data: any): FormDataRecord {
    const newRecord: FormDataRecord = {
      id: this.guidService.generateGuid(),
      formName: formName,
      data: data,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    if (!this.mockData[formName]) {
      this.mockData[formName] = [];
    }

    this.mockData[formName].push(newRecord);
    this.saveToLocalStorage();

    return newRecord;
  }

  // 更新表單記錄
  updateFormRecord(recordId: string, data: any): FormDataRecord | null {
    let updatedRecord: FormDataRecord | null = null;

    Object.keys(this.mockData).forEach(formName => {
      const recordIndex = this.mockData[formName].findIndex(record => record.id === recordId);
      if (recordIndex !== -1) {
        this.mockData[formName][recordIndex] = {
          ...this.mockData[formName][recordIndex],
          data: data,
          updatedAt: new Date()
        };
        updatedRecord = this.mockData[formName][recordIndex];
      }
    });

    if (updatedRecord) {
      this.saveToLocalStorage();
    }

    return updatedRecord;
  }

  // 刪除表單記錄
  deleteFormRecord(recordId: string): boolean {
    let deleted = false;

    Object.keys(this.mockData).forEach(formName => {
      const recordIndex = this.mockData[formName].findIndex(record => record.id === recordId);
      if (recordIndex !== -1) {
        this.mockData[formName].splice(recordIndex, 1);
        deleted = true;
      }
    });

    if (deleted) {
      this.saveToLocalStorage();
    }

    return deleted;
  }

  // 取得指定記錄
  getFormRecord(recordId: string): FormDataRecord | null {
    for (const formName of Object.keys(this.mockData)) {
      const record = this.mockData[formName].find(record => record.id === recordId);
      if (record) {
        return record;
      }
    }
    return null;
  }

  // 取得表單配置（Mock 資料）
  getFormConfigs(): FormConfig[] {
    return [

   {
  formid: '6d755ec1-5f87-4f66-b6f1-14f0e3167624',
  formTitle: '工作場所環境及危害因素告知單'
},

{
  formid: '125f6568-d76d-4886-8211-bf72345dddba',
  formTitle: '動火作業-許可證'
},
{
  formid: 'eaa426ec-ff8b-4681-bcdb-b7fcd55e49b2',
  formTitle: '高架作業-許可證'
},
{
  formid: 'a22f011b-9155-4e8e-8744-871ef4ac5986',
  formTitle: '局限空間作業-許可證'
},
{
  formid: '5513327b-df14-4eca-b347-ec67554538fc',
  formTitle: '吊掛作業-許可證'
},
{
  formid: '0e0d7aec-22d2-4bf0-899d-f90c9d14ea08',
  formTitle: '有機溶劑作業-許可證'
},

{
  formid: '1f5b1ca4-776e-41b9-99ff-50a38e9ec1a5',
  formTitle: '特殊作業-許可證'
},
//    {
//   formid: '46ae505d-6765-4971-a1e8-4c2c6464a891',
//   formName: '每日入廠作業危害告知與健康確認表',
//   formTitle: '每日入廠作業危害告知與健康確認表'
// },
// {
//   formid: 'c4379d1c-17e0-4288-ae0e-07e0b7a31506',
//   formName: '動火作業(1)作業前檢點表',
//   formTitle: '動火作業(1)作業前檢點表'
// },
// {
//   formid: 'a8a418f5-085f-4dfc-bebb-9ce84845af6c',
//   formName: '動火作業(2)動火作業檢點表',
//   formTitle: '動火作業(2)動火作業檢點表'
// },
// {
//   formid: 'c7356730-178b-425c-a1d5-a3835b6168c8',
//   formName: '高架作業檢點表',
//   formTitle: '高架作業檢點表'
// },

// {
//   formid: '549b163d-eea3-4df5-b7f5-7351480bee15',
//   formName: '吊掛作業檢點表-檢點表',
//   formTitle: '吊掛作業檢點表-檢點表'
// },
// {
//   formid: 'a416fd2b-bbf3-4ad7-a50a-f426c046e392',
//   formName: '局限空間作業-(3)環境測定紀錄',
//   formTitle: '局限空間作業-(3)環境測定紀錄'
// },
// {
//   formid: 'afd3da67-0ec3-41e7-8d39-11c24116b9a9',
//   formName: '局限空間作業-(1)作業檢點表',
//   formTitle: '局限空間作業-(1)作業檢點表'
// },
// {
//   formid: '0cd83a20-5ff5-4c0b-8de4-f74747bbdbae',
//   formName: '局限空間作業-(2)人員進出管制表',
//   formTitle: '局限空間作業-(2)人員進出管制表'
// },


// {
//   formid: '1f741029-0b21-40e0-aa07-2d624916a036',
//   formName: '有機溶劑每日檢點表',
//   formTitle: '有機溶劑每日檢點表'
// },
// {
//   formid: '49694d1f-de87-4bbf-89e7-d1f92b57b338',
//   formName: '每日入廠巡檢紀錄-作業前/作業中',
//   formTitle: '每日入廠巡檢紀錄-作業前/作業中'
// },
// {
//   formid: '6d755ec1-5f87-4f66-b6f1-14f0e3167624',
//   formName: '工作場所環境及危害因素告知單',
//   formTitle: '工作場所環境及危害因素告知單'
// }


    ];
  }

  // 儲存資料到 localStorage
  private saveToLocalStorage(): void {
    try {
      localStorage.setItem(this.formDataKey, JSON.stringify(this.mockData));
    } catch (error) {
      console.error('儲存表單資料失敗:', error);
    }
  }

  // 清除所有資料（用於測試）
  clearAllData(): void {
    this.mockData = {};
    localStorage.removeItem(this.formDataKey);
  }
}
